// ============================================
// Suppliers Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useSuppliersService } from '../providers/ApiProvider';
import type { Supplier } from '@conai/api-client';
import { queryKeys } from '../config/query-client';
import { api } from '../config/api';
import { useI18n } from '../i18n';

type SupplierListParams = {
  page?: number;
  page_size?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'pending';
  is_active?: boolean;
  payment_method?: string;
  city?: string;
  ordering?: string;
  [key: string]: unknown;
};

type CreateSupplierRequest = {
  code?: string;
  name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  is_active?: boolean;
  city?: string;
  country?: string;
  tax_id?: string;
  payment_method?: string;
  payment_terms?: string;
  lead_time_days?: number;
  minimum_order_value?: string;
  notes?: string;
};

type UpdateSupplierRequest = Partial<CreateSupplierRequest>;

// ============================================
// Query Hooks
// ============================================

/**
 * `?is_active` của `/suppliers/` chỉ nhận boolean.
 *
 * BE nay trả **400** cho giá trị lạ (`?is_active=abc`). Trước đây giá trị sai bị
 * hiểu ngầm thành `false`, nghĩa là gõ nhầm param lại trả về đúng danh sách
 * INACTIVE — ngược hẳn ý người gọi và không ai nhận ra. Nên ở đây bất cứ thứ gì
 * không phải boolean (chuỗi, `null`, số...) đều bị BỎ HẲN khỏi params: không
 * truyền param = BE trả cả active lẫn inactive, mặc định an toàn duy nhất.
 *
 * Lưu ý `cleanParams` của api-client chỉ loại `undefined` — `null` vẫn lọt
 * xuống query string, nên phải chặn ở đây.
 *
 * Chuẩn hoá ngay tại hook (chứ không chỉ lúc gửi) để `queryKey` và query string
 * luôn khớp: nếu không, hai lời gọi chỉ khác nhau ở một giá trị rác sẽ ra hai
 * cache entry cho cùng một response.
 */
function normalizeSupplierParams(params: SupplierListParams): SupplierListParams {
  if (!('is_active' in params) || typeof params.is_active === 'boolean') {
    return params;
  }
  const rest: SupplierListParams = { ...params };
  delete rest.is_active;
  return rest;
}

/**
 * Hook to list suppliers with pagination and filters
 */
export function useSuppliers(params: SupplierListParams = {}) {
  const suppliersService = useSuppliersService();
  const safeParams = normalizeSupplierParams(params);

  return useQuery({
    // `queryKey` ôm trọn params nên hai danh sách (có `is_active: true` và không
    // lọc) là hai cache entry riêng — không thể lẫn dữ liệu của nhau.
    queryKey: queryKeys.suppliers.list(safeParams as Record<string, unknown>),
    queryFn: () => suppliersService.list(safeParams),
  });
}

/**
 * Hook to get a supplier by ID
 */
export function useSupplier(id: string, options?: { enabled?: boolean }) {
  const suppliersService = useSuppliersService();

  return useQuery({
    queryKey: queryKeys.suppliers.detail(id),
    queryFn: () => suppliersService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to search suppliers
 */
export function useSearchSuppliers(query: string, params?: Omit<SupplierListParams, 'search'>) {
  const suppliersService = useSuppliersService();
  const safeParams = normalizeSupplierParams(params ?? {}) as Omit<SupplierListParams, 'search'>;

  return useQuery({
    queryKey: queryKeys.suppliers.list({ ...safeParams, search: query }),
    queryFn: () => suppliersService.search(query, safeParams),
    enabled: query.length >= 2,
  });
}

/**
 * Hook to get active suppliers
 */
export function useActiveSuppliers(params?: Omit<SupplierListParams, 'is_active'>) {
  const suppliersService = useSuppliersService();

  return useQuery({
    queryKey: queryKeys.suppliers.list({ ...params, is_active: true }),
    queryFn: () => suppliersService.getActive(params),
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new supplier
 */
export function useCreateSupplier() {
  const suppliersService = useSuppliersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateSupplierRequest) => suppliersService.create(data) as Promise<Supplier>,
    onSuccess: (supplier: Supplier) => {
      // Bug #186: invalidate CẢ NHÁNH `suppliers` — danh sách, chi tiết và thẻ
      // thống kê cùng lúc. Chỉ invalidate `lists()` thì thẻ KPI nằm ngoài phạm vi.
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      toast.success(t('toastSupplierCreated', { name: supplier.name }));
    },
  });
}

/**
 * Hook to update a supplier
 */
export function useUpdateSupplier() {
  const suppliersService = useSuppliersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierRequest }) =>
      suppliersService.update(id, data) as Promise<Supplier>,
    onSuccess: (supplier: Supplier) => {
      // Bug #186: invalidate CẢ NHÁNH `suppliers` — danh sách, chi tiết và thẻ
      // thống kê cùng lúc. Chỉ invalidate `lists()` thì thẻ KPI nằm ngoài phạm vi.
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(supplier.id) });
      toast.success(t('toastSupplierUpdated', { name: supplier.name }));
    },
  });
}

/**
 * Hook to delete a supplier
 */
export function useDeleteSupplier() {
  const suppliersService = useSuppliersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => suppliersService.delete(id),
    onSuccess: () => {
      // Bug #186: invalidate CẢ NHÁNH `suppliers` — danh sách, chi tiết và thẻ
      // thống kê cùng lúc. Chỉ invalidate `lists()` thì thẻ KPI nằm ngoài phạm vi.
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      toast.success(t('toastSupplierDeleted'));
    },
  });
}

/**
 * Hook to activate a supplier
 */
export function useActivateSupplier() {
  const suppliersService = useSuppliersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => suppliersService.activate(id) as Promise<Supplier>,
    onSuccess: (supplier: Supplier) => {
      // Bug #186: invalidate CẢ NHÁNH `suppliers` — danh sách, chi tiết và thẻ
      // thống kê cùng lúc. Chỉ invalidate `lists()` thì thẻ KPI nằm ngoài phạm vi.
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(supplier.id) });
      toast.success(t('toastSupplierActivated', { name: supplier.name }));
    },
  });
}

/**
 * Hook to deactivate a supplier
 */
export function useDeactivateSupplier() {
  const suppliersService = useSuppliersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => suppliersService.deactivate(id) as Promise<Supplier>,
    onSuccess: (supplier: Supplier) => {
      // Bug #186: invalidate CẢ NHÁNH `suppliers` — danh sách, chi tiết và thẻ
      // thống kê cùng lúc. Chỉ invalidate `lists()` thì thẻ KPI nằm ngoài phạm vi.
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(supplier.id) });
      toast.success(t('toastSupplierDeactivated', { name: supplier.name }));
    },
  });
}

// ============================================
// Stats Hook
// ============================================

/**
 * Hook to get supplier statistics
 */
export function useSuppliersStats() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.suppliers.stats(),
    queryFn: () => api.client.get<Record<string, unknown>>('/suppliers/stats/'),
    // Bug #186: `staleTime: 60s` cũ khiến kể cả khi có invalidate thì số vẫn kẹt
    // tới một phút. Số nhà cung cấp thay đổi ngay lúc người dùng vừa xoá — không
    // có lý do gì để giữ bản cũ.
    staleTime: 0,
  });

  // Bug #186 (vòng 2 — QA bắt trên UI): thẻ "Total Suppliers" hiện 5 trong khi
  // danh sách có 2. Fix cache ở vòng 1 làm thẻ refetch ĐÚNG LÚC, nhưng con số
  // refetch về vẫn sai: `/suppliers/stats/` của BE đếm khác `/suppliers/` —
  // khớp với việc nó đếm cả supplier đã XOÁ MỀM (5 = 2 còn sống + 3 QA đã xoá).
  //
  // Hai con số đứng cạnh nhau trên một màn hình phải ra từ MỘT nguồn. Total và
  // Active giờ đếm bằng chính endpoint danh sách (page_size=1, chỉ lấy `count`)
  // — thẻ và bảng không thể lệch nhau nữa, BE stats đếm kiểu gì cũng không ảnh
  // hưởng. Stats endpoint chỉ còn phục vụ hai số danh sách không tự tính được:
  // avg delivery time và pending payment. Đã ghi vào lời nhắn BE: stats cần loại
  // bản ghi xoá mềm.
  const { data: totalCountData } = useQuery({
    queryKey: queryKeys.suppliers.list({ kpi: 'total-count' }),
    queryFn: () =>
      api.client.get<Record<string, unknown>>('/suppliers/', { page: 1, page_size: 1 }),
    staleTime: 0,
  });
  const { data: activeCountData } = useQuery({
    queryKey: queryKeys.suppliers.list({ kpi: 'active-count' }),
    queryFn: () =>
      api.client.get<Record<string, unknown>>('/suppliers/', { page: 1, page_size: 1, is_active: true }),
    staleTime: 0,
  });

  const readCount = (payload: unknown): number | null => {
    const raw = ((payload as any)?.data ?? payload ?? {}) as Record<string, any>;
    const count = Number(raw.count ?? raw.total ?? raw.meta?.count ?? NaN);
    return Number.isFinite(count) ? count : null;
  };

  const d = ((data as any)?.data ?? data ?? {}) as Record<string, any>;
  // Đếm từ danh sách là nguồn chính; stats chỉ là fallback lúc query đếm chưa về.
  const total = readCount(totalCountData) ?? Number(d.total ?? 0);
  const active = readCount(activeCountData) ?? Number(d.active ?? 0);
  const stats = {
    total,
    active,
    inactive: Math.max(0, total - active),
    avgDeliveryDays: Number(d.avg_delivery_days ?? 0),
    pendingPayment: Number(d.pending_payment ?? 0),
  };
  return { stats, isLoading };
}
